import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from "react-router-dom";
import { shouldFilterKnownNoise } from "utils/sentry/shouldFilterKnownNoise";
import i18n from "i18n";
import { installSentryPrivacy, scrubBreadcrumb, scrubEvent, scrubRecordingEvent, scrubSpan, scrubTransactionEvent } from "utils/sentry/privacyHooks";
import { installReplayGate, prepareReplayGate } from "utils/sentry/replayGate";
import { REPLAY_PRIVACY_OPTIONS } from "utils/sentry/replayPrivacy";

const allowedSentryHosts = new Set(["app.refabric.com"]);
const sentryEnabled = !!process.env.REACT_APP_SENTRY_DSN && allowedSentryHosts.has(window.location.hostname);

// Custom recording events are scrubbed by the callback (privacy B1). The rrweb Meta `href` cannot be (D-B1), so
// this integration is NOT passed to init: `installReplayGate` registers it only while the URL is safe and stops it
// on a secret one (utils/sentry/replayGate). Rates are exactly as before. DOM attributes on a safe page (a token link,
// a signed `src`, a URL in a `data-*`) are kept out by the pinned privacy options (utils/sentry/replayPrivacy, U-1).
// Resumable-upload session ids are bearer write handles — scrubRecordingEvent removes them first (privacyHooks).
const replay = Sentry.replayIntegration({ ...REPLAY_PRIVACY_OPTIONS, beforeAddRecordingEvent: scrubRecordingEvent });
// Before init: the gate's `popstate` / `hashchange` listeners must run before Sentry's own (see replayGate).
prepareReplayGate();

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  release: process.env.REACT_APP_VERSION || undefined,
  environment: sentryEnabled ? process.env.REACT_APP_SENTRY_ENVIRONMENT || "development" : "development",
  enabled: sentryEnabled,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  beforeSend(event) {
    const values = event.exception?.values ?? [];
    const frames = values.flatMap((v) => v.stacktrace?.frames ?? []);

    // Filter New Relic internal errors
    if (frames.some((frame) => frame.filename?.includes("/nr-"))) {
      return null;
    }

    // Filter third-party DOM mutation errors (React #17256)
    if (values.some((v) => v.type === "NotFoundError" && (v.value?.includes("removeChild") || v.value?.includes("insertBefore")))) {
      return null;
    }

    // Filter ChunkLoadError — expected during deploys, auto-recovered by ErrorBoundary reload
    if (values.some((v) => v.type === "ChunkLoadError" || v.value?.includes("Loading chunk") || v.value?.includes("Loading CSS chunk"))) {
      return null;
    }

    // Filter the benign "ResizeObserver loop …" error — not a crash, the observation just
    // continues next frame (common with xyflow NodeResizer + a resizing child). Also swallowed
    // at the window-error level in utils/domPatch, but wrappers can still surface it to Sentry.
    if (values.some((v) => v.value?.startsWith("ResizeObserver loop"))) {
      return null;
    }

    // Filter axios "Network Error" — client-side connectivity loss (XHR status 0), not actionable.
    // Axios emits this literal string only when no HTTP response was received; real 5xx errors produce
    // "Request failed with status code NNN" and are not affected.
    if (values.some((v) => v.value === "Network Error" || v.value === "Unhandled Promise Rejection: Network Error")) {
      return null;
    }

    // Filter Firebase SDK internal assertion errors. Known issue in firebase-js-sdk (e.g. #8061) on Safari
    // where the SDK's IndexedDB / auth-state persistence rejects a background promise we never await.
    // 0 in-app frames, not actionable from our code.
    if (values.some((v) => v.value?.includes("INTERNAL ASSERTION FAILED"))) {
      return null;
    }

    // Filter browser-extension / injected-script errors — not our code (0 in-app frames, varied
    // user agents). "Object Not Found Matching Id:N, MethodName:..." is a known unhandled rejection
    // from certain extensions / embedded webviews; "EmptyRanges" is a Safari extension / content-blocker
    // ReferenceError. Neither originates in our bundle and neither is actionable. (REFABRIC-FRONTEND-23 / -32)
    if (values.some((v) => v.value?.includes("Object Not Found Matching Id") || v.value?.includes("EmptyRanges"))) {
      return null;
    }

    // Filter cancelled requests (route changes, component unmounts) and Hotjar's Firefox
    // localStorage NS_ERRORs — see utils/sentry/shouldFilterKnownNoise.
    if (shouldFilterKnownNoise(event)) {
      return null;
    }

    // Defense in depth: drop intentional UX statuses if a caller ever tags them as http errors.
    // The HttpClient interceptor already prevents this, but a future manual reportError call could slip through.
    const tags = (event.tags ?? {}) as Record<string, string>;
    if (tags.error_source === "http" && ["401", "402", "403", "409", "503"].includes(tags.http_status)) {
      return null;
    }

    event.tags = { ...event.tags, locale: i18n.language || "en" };

    // Privacy B1 (REF-6708): URLs, routes and paths in every telemetry copy go through the shared
    // policy (utils/privacy/telemetryUrl). Last, so nothing added above escapes.
    return scrubEvent(event);
  },
  beforeSendTransaction: scrubTransactionEvent,
  // Photoshoot Studio ZIP chunks go to a GCS resumable session url through the instrumented `fetch`; scrubSpan
  // and scrubBreadcrumb remove its `upload_id` first (utils/sentry/scrubUploadSession, folded into privacyHooks).
  beforeSendSpan: scrubSpan,
  beforeBreadcrumb: scrubBreadcrumb,
});

// After init: the envelope-header DSC (`createDsc`) and `replay_event` (no beforeSend) — see privacyHooks.
installSentryPrivacy(Sentry.getClient());

// Session Replay records only while the URL is safe (D-B1 option 2) — after the privacy hooks are in place.
installReplayGate(replay);
